import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Progress } from '../ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { 
  Target, Plus, Loader2, Edit, Trash2, CheckCircle, Clock, 
  AlertCircle, Calendar as CalendarIcon, TrendingUp, Users,
  Award, FileText, Eye
} from 'lucide-react';
import { performanceApi, employeeApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { useManagerViewPermission } from '../../hooks/useManagerViewPermission';

type UserRole = 'employee' | 'manager';
type PerformanceStage = 'not-started' | 'goals-set' | 'self-review' | '1-1-alignment' | 'manager-review' | 'completed';

interface GoalFormData {
  title: string;
  description: string;
  target_date: string;
  status: string;
  progress: number;
  weight: number;
  category: string;
}

const PerformanceNew: React.FC = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  // User context (in real app, get from auth)
  const currentUserId = 'emp-1';
  const userRole: UserRole = 'manager'; // or 'manager'
  
  const { isUserView, canViewTeamPerformance } = useManagerViewPermission();
  const effectiveRole = userRole === 'manager' && !isUserView ? 'manager' : 'employee';
  
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [activeTab, setActiveTab] = useState('my-performance');
  const [isGoalDialogOpen, setIsGoalDialogOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState<any>(null);
  const [goalFormData, setGoalFormData] = useState<GoalFormData>({
    title: '',
    description: '',
    target_date: '',
    status: 'NOT_STARTED',
    progress: 0,
    weight: 10,
    category: 'individual'
  });

  // Available years
  const availableYears = [2022, 2023, 2024, 2025];

  // Fetch goals for the selected year
  const { data: goals = [], isLoading: goalsLoading } = useQuery({
    queryKey: ['performanceGoals', currentUserId, selectedYear],
    queryFn: () => performanceApi.goals.getAll({ employee_id: currentUserId, year: selectedYear }),
  });

  // Fetch reviews for the selected year
  const { data: reviews = [], isLoading: reviewsLoading } = useQuery({
    queryKey: ['performanceReviews', currentUserId, selectedYear],
    queryFn: () => performanceApi.reviews.getAll({ employee_id: currentUserId, year: selectedYear }),
  });

  // Fetch employees (for manager view)
  const { data: employees = [] } = useQuery({
    queryKey: ['employees'],
    queryFn: () => employeeApi.getAll({}),
    enabled: effectiveRole === 'manager',
  });

  // Fetch team goals (for manager view)
  const { data: teamGoals = [] } = useQuery({
    queryKey: ['teamPerformanceGoals', selectedYear],
    queryFn: () => performanceApi.goals.getAll({ year: selectedYear }),
    enabled: effectiveRole === 'manager',
  });

  const createGoalMutation = useMutation({
    mutationFn: performanceApi.goals.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['performanceGoals'] });
      setIsGoalDialogOpen(false);
      resetGoalForm();
      toast({ title: 'Goal created successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const updateGoalMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => performanceApi.goals.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['performanceGoals'] });
      setIsGoalDialogOpen(false);
      resetGoalForm();
      toast({ title: 'Goal updated successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const deleteGoalMutation = useMutation({
    mutationFn: performanceApi.goals.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['performanceGoals'] });
      toast({ title: 'Goal deleted successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const handleSaveGoal = () => {
    if (!goalFormData.title || !goalFormData.target_date) {
      toast({ title: 'Error', description: 'Please fill required fields', variant: 'destructive' });
      return;
    }

    const goalData = {
      ...goalFormData,
      employee_id: currentUserId,
      year: selectedYear,
    };

    if (selectedGoal) {
      updateGoalMutation.mutate({ id: selectedGoal.id, data: goalData });
    } else {
      createGoalMutation.mutate(goalData);
    }
  };

  const handleEditGoal = (goal: any) => {
    setSelectedGoal(goal);
    setGoalFormData({
      title: goal.title,
      description: goal.description || '',
      target_date: goal.target_date,
      status: goal.status,
      progress: goal.progress || 0,
      weight: goal.weight || 10,
      category: goal.category || 'individual',
    });
    setIsGoalDialogOpen(true);
  };

  const handleDeleteGoal = (goalId: string) => {
    if (confirm('Are you sure you want to delete this goal?')) {
      deleteGoalMutation.mutate(goalId);
    }
  };

  const resetGoalForm = () => {
    setSelectedGoal(null);
    setGoalFormData({
      title: '',
      description: '',
      target_date: '',
      status: 'NOT_STARTED',
      progress: 0,
      weight: 10,
      category: 'individual',
    });
  };

  // Calculate performance stage
  const getPerformanceStage = (): PerformanceStage => {
    if (goals.length === 0) return 'not-started';
    if (reviews.length === 0) return 'goals-set';
    
    const hasCompletedReview = reviews.some((r: any) => r.status === 'COMPLETED');
    if (hasCompletedReview) return 'completed';
    
    const hasSelfReview = reviews.some((r: any) => r.review_type === 'SELF');
    if (!hasSelfReview) return 'self-review';
    
    const hasManagerReview = reviews.some((r: any) => r.review_type === 'MANAGER');
    if (!hasManagerReview) return 'manager-review';
    
    return '1-1-alignment';
  };

  const performanceStage = getPerformanceStage();

  // Calculate stats
  const totalGoals = goals.length;
  const completedGoals = goals.filter((g: any) => g.status === 'COMPLETED').length;
  const overallProgress = totalGoals > 0 ? Math.round((completedGoals / totalGoals) * 100) : 0;
  const averageScore = reviews.length > 0 
    ? (reviews.reduce((sum: number, r: any) => sum + (r.overall_rating || 0), 0) / reviews.length).toFixed(1)
    : 'N/A';

  const getStatusBadgeVariant = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'COMPLETED': return 'default';
      case 'IN_PROGRESS': return 'secondary';
      case 'NOT_STARTED': return 'outline';
      case 'OVERDUE': return 'destructive';
      default: return 'secondary';
    }
  };

  const getStageBadge = (stage: PerformanceStage) => {
    const stageInfo = {
      'not-started': { label: 'Not Started', icon: AlertCircle, color: 'text-gray-500' },
      'goals-set': { label: 'Goals Set', icon: Target, color: 'text-blue-500' },
      'self-review': { label: 'Self Review Pending', icon: FileText, color: 'text-orange-500' },
      '1-1-alignment': { label: '1:1 Alignment', icon: Users, color: 'text-purple-500' },
      'manager-review': { label: 'Manager Review', icon: CheckCircle, color: 'text-indigo-500' },
      'completed': { label: 'Completed', icon: Award, color: 'text-green-500' },
    };

    const info = stageInfo[stage];
    const Icon = info.icon;

    return (
      <div className="flex items-center space-x-2">
        <Icon className={`h-5 w-5 ${info.color}`} />
        <span className={`font-medium ${info.color}`}>{info.label}</span>
      </div>
    );
  };

  const getEmployeeName = (employeeId: string) => {
    const emp = employees.find((e: any) => e.id === employeeId);
    if (!emp) return 'Unknown';
    return `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
  };

  const getEmployeeInitials = (employeeId: string) => {
    const emp = employees.find((e: any) => e.id === employeeId);
    if (!emp) return 'U';
    const first = emp.first_name?.charAt(0) || '';
    const last = emp.last_name?.charAt(0) || '';
    return (first + last).toUpperCase() || 'U';
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (goalsLoading || reviewsLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading performance data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumbs */}
      <nav className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
        <span>Performance</span>
        <span>/</span>
        <span className="text-gray-900 dark:text-white font-medium">
          {effectiveRole === 'manager' ? 'Team Performance' : 'My Performance'}
        </span>
      </nav>

      {/* Header with Year Selection */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {effectiveRole === 'manager' ? 'Team Performance' : 'My Performance'}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {effectiveRole === 'manager' 
              ? 'Review and manage team performance goals and reviews' 
              : 'Track your goals and performance for the year'}
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <CalendarIcon className="h-4 w-4 text-muted-foreground" />
            <Select value={selectedYear.toString()} onValueChange={(val) => setSelectedYear(parseInt(val))}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableYears.map(year => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {effectiveRole === 'employee' && activeTab === 'my-performance' && (
            <Button onClick={() => setIsGoalDialogOpen(true)} className="bg-primary hover:bg-primary-hover">
              <Plus className="h-4 w-4 mr-2" />
              Add Goal
            </Button>
          )}
        </div>
      </div>

      {/* Tabs for Manager/Employee */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          {effectiveRole === 'employee' ? (
            <>
              <TabsTrigger value="my-performance">My Performance</TabsTrigger>
              <TabsTrigger value="reviews">Reviews</TabsTrigger>
            </>
          ) : (
            <>
              <TabsTrigger value="my-performance">Overview</TabsTrigger>
              <TabsTrigger value="team-goals">Team Goals</TabsTrigger>
              <TabsTrigger value="pending-reviews">Pending Reviews</TabsTrigger>
            </>
          )}
        </TabsList>

        {/* Employee View */}
        {effectiveRole === 'employee' && (
          <>
            <TabsContent value="my-performance" className="space-y-6">
              {/* Performance Stage & Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="md:col-span-2">
                  <CardContent className="pt-6">
                    <Label className="text-sm text-muted-foreground">Current Stage</Label>
                    <div className="mt-2">
                      {getStageBadge(performanceStage)}
                    </div>
                    <div className="mt-4 space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Progress</span>
                        <span className="font-medium">{completedGoals}/{totalGoals} goals completed</span>
                      </div>
                      <Progress value={overallProgress} className="h-2" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Total Goals</p>
                        <p className="text-3xl font-bold mt-1">{totalGoals}</p>
                      </div>
                      <Target className="h-8 w-8 text-primary" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Overall Score</p>
                        <p className="text-3xl font-bold mt-1">{averageScore}</p>
                      </div>
                      <TrendingUp className="h-8 w-8 text-green-500" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Performance Stages Timeline */}
              <Card>
                <CardHeader>
                  <CardTitle>Performance Review Process</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    {[
                      { stage: 'goals-set', label: 'Set Goals' },
                      { stage: 'self-review', label: 'Self Review' },
                      { stage: '1-1-alignment', label: '1:1 Alignment' },
                      { stage: 'manager-review', label: 'Manager Review' },
                      { stage: 'completed', label: 'Complete' },
                    ].map((item, index) => {
                      const isCompleted = ['goals-set', 'self-review', '1-1-alignment', 'manager-review', 'completed']
                        .indexOf(performanceStage) >= index;
                      const isCurrent = ['goals-set', 'self-review', '1-1-alignment', 'manager-review', 'completed']
                        .indexOf(performanceStage) === index;

                      return (
                        <div key={item.stage} className="flex items-center">
                          <div className="flex flex-col items-center">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              isCompleted ? 'bg-primary text-white' : 
                              isCurrent ? 'bg-orange-500 text-white' : 
                              'bg-gray-200 dark:bg-gray-700 text-gray-500'
                            }`}>
                              {isCompleted ? (
                                <CheckCircle className="h-5 w-5" />
                              ) : isCurrent ? (
                                <Clock className="h-5 w-5" />
                              ) : (
                                <span className="text-sm font-medium">{index + 1}</span>
                              )}
                            </div>
                            <p className={`text-xs mt-2 text-center ${
                              isCurrent ? 'font-semibold text-orange-500' : 'text-muted-foreground'
                            }`}>
                              {item.label}
                            </p>
                          </div>
                          {index < 4 && (
                            <div className={`h-0.5 w-16 mx-2 ${
                              isCompleted && index < ['goals-set', 'self-review', '1-1-alignment', 'manager-review', 'completed'].indexOf(performanceStage)
                                ? 'bg-primary' 
                                : 'bg-gray-200 dark:bg-gray-700'
                            }`}></div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Goals Table */}
              <Card>
                <CardHeader>
                  <CardTitle>My Goals for {selectedYear}</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Goal</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Target Date</TableHead>
                        <TableHead>Progress</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {goals.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                            No goals set for {selectedYear}. Click "Add Goal" to get started.
                          </TableCell>
                        </TableRow>
                      ) : (
                        goals.map((goal: any) => (
                          <TableRow key={goal.id}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{goal.title}</p>
                                <p className="text-xs text-muted-foreground mt-1">{goal.description}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{goal.category || 'Individual'}</Badge>
                            </TableCell>
                            <TableCell>{formatDate(goal.target_date)}</TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                <div className="flex items-center justify-between text-sm">
                                  <span>{goal.progress || 0}%</span>
                                </div>
                                <Progress value={goal.progress || 0} className="h-2" />
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant={getStatusBadgeVariant(goal.status)}>
                                {goal.status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center space-x-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditGoal(goal)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteGoal(goal.id)}
                                  className="text-destructive"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="reviews" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Performance Reviews for {selectedYear}</CardTitle>
                </CardHeader>
                <CardContent>
                  {reviews.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No performance reviews available for {selectedYear}.
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {reviews.map((review: any) => (
                        <div key={review.id} className="border rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <h3 className="font-semibold">{review.review_period || `${selectedYear} Review`}</h3>
                              <p className="text-sm text-muted-foreground">
                                Reviewed by: {review.reviewer_id ? getEmployeeName(review.reviewer_id) : 'N/A'}
                              </p>
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-bold text-primary">{review.overall_rating || 'N/A'}</div>
                              <p className="text-xs text-muted-foreground">out of 5.0</p>
                            </div>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {review.comments || 'No comments provided'}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </>
        )}

        {/* Manager View */}
        {effectiveRole === 'manager' && (
          <>
            <TabsContent value="my-performance" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground">Team Members</p>
                    <p className="text-3xl font-bold mt-1">{employees.length}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground">Pending Reviews</p>
                    <p className="text-3xl font-bold text-orange-600 mt-1">3</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground">Team Avg Score</p>
                    <p className="text-3xl font-bold text-green-600 mt-1">4.2</p>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Team Performance Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead>Goals</TableHead>
                        <TableHead>Progress</TableHead>
                        <TableHead>Stage</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {employees.slice(0, 5).map((emp: any) => (
                        <TableRow key={emp.id}>
                          <TableCell>
                            <div className="flex items-center space-x-3">
                              <Avatar className="h-8 w-8">
                                <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                                  {getEmployeeInitials(emp.id)}
                                </AvatarFallback>
                              </Avatar>
                              <span className="font-medium">{getEmployeeName(emp.id)}</span>
                            </div>
                          </TableCell>
                          <TableCell>8 / 10</TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="flex items-center justify-between text-sm">
                                <span>75%</span>
                              </div>
                              <Progress value={75} className="h-2" />
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">Self Review</Badge>
                          </TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm">
                              <Eye className="h-4 w-4 mr-1" />
                              Review
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="team-goals" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>All Team Goals for {selectedYear}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-center py-8">
                    Team goals view - showing all employee goals across the team
                  </p>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="pending-reviews" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Pending Performance Reviews</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-center py-8">
                    List of employees requiring manager review
                  </p>
                </CardContent>
              </Card>
            </TabsContent>
          </>
        )}
      </Tabs>

      {/* Goal Dialog */}
      <Dialog open={isGoalDialogOpen} onOpenChange={setIsGoalDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedGoal ? 'Edit Goal' : 'Add New Goal'}</DialogTitle>
            <DialogDescription>
              {selectedGoal ? 'Update your goal details' : 'Set a new performance goal for the year'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Goal Title *</Label>
              <Input
                placeholder="e.g., Complete Project X"
                value={goalFormData.title}
                onChange={(e) => setGoalFormData(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Describe your goal in detail..."
                value={goalFormData.description}
                onChange={(e) => setGoalFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={goalFormData.category}
                  onValueChange={(value) => setGoalFormData(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">Individual</SelectItem>
                    <SelectItem value="team">Team</SelectItem>
                    <SelectItem value="company">Company</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Target Date *</Label>
                <Input
                  type="date"
                  value={goalFormData.target_date}
                  onChange={(e) => setGoalFormData(prev => ({ ...prev, target_date: e.target.value }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={goalFormData.status}
                  onValueChange={(value) => setGoalFormData(prev => ({ ...prev, status: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NOT_STARTED">Not Started</SelectItem>
                    <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                    <SelectItem value="COMPLETED">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Progress (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={goalFormData.progress === 0 ? '' : goalFormData.progress}
                  onChange={(e) => {
                    const value = e.target.value;
                    setGoalFormData(prev => ({ 
                      ...prev, 
                      progress: value === '' ? 0 : parseInt(value) || 0 
                    }));
                  }}
                  onBlur={(e) => {
                    if (e.target.value === '') {
                      setGoalFormData(prev => ({ ...prev, progress: 0 }));
                    }
                  }}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsGoalDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveGoal}
              disabled={createGoalMutation.isPending || updateGoalMutation.isPending}
              className="bg-primary hover:bg-primary-hover"
            >
              {(createGoalMutation.isPending || updateGoalMutation.isPending) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {selectedGoal ? 'Update Goal' : 'Create Goal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PerformanceNew;
